import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import SvgIcon from "../../assets/icons/SvgIcon";
import "./tab.scss";

interface TabBodyProps {
  children: React.ReactNode;
  eventKey: string;
  title: string;
  tabIcon?: string;
  className?: string;
  isActive?: boolean;
  isHide?: boolean;
}

export const TabBody: React.FC<TabBodyProps> = ({
  children,
  isActive,
  eventKey,
  tabIcon = "",
  title,
  className = "",
}) => {
  return (
    <div
      className={`${isActive ? "flex justify-center" : "hidden"} ${className} ${eventKey} ${title} ${tabIcon}`}
    >
      {children}
    </div>
  );
};

type asLinkType = {
  title: string;
  url: string;
  icon?: string;
};

interface TabsBoxPropsWithoutLink {
  children: React.ReactNode;
  asLink: false;
  defaultEventKey: string;
  currentTab: (currentTab: string) => void;
}

interface TabsBoxPropsWithLink {
  children: React.ReactNode;
  asLink?: asLinkType[];
  defaultEventKey?: string;
  currentTab?: (currentTab: string) => void;
}

type TabsBoxProps = TabsBoxPropsWithoutLink | TabsBoxPropsWithLink;

const TabsBox: React.FC<TabsBoxProps> = ({
  children,
  defaultEventKey,
  currentTab,
  asLink = false,
}) => {
  const [activeKey, setActiveKey] = useState(defaultEventKey);
  const [visitedTabs, setVisitedTabs] = useState(new Set([defaultEventKey]));

  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const scrollLeft = () => {
    scrollContainerRef.current?.scrollBy({ left: -200, behavior: "smooth" });
  };

  const scrollRight = () => {
    scrollContainerRef.current?.scrollBy({ left: 200, behavior: "smooth" });
  };

  const getShowButtons = useMemo(() => {
    if (scrollContainerRef.current) {
      const { scrollWidth, clientWidth } = scrollContainerRef.current;
      return scrollWidth > clientWidth;
    } else {
      return false;
    }
  }, [children]);

  const handleTabClick = (eventKey: string) => {
    setActiveKey(eventKey);
    setVisitedTabs((prev) => new Set([...prev, eventKey])); // Store visited tabs
    currentTab && currentTab(eventKey);
  };

  useEffect(() => {
    setActiveKey(defaultEventKey);
  }, [defaultEventKey]);

  if (asLink) {
    return (
      <div className="relative">
        <div className="top-0 sticky border-gray-200 border-b w-full overflow-x-auto">
          <div className="flex justify-start items-center gap-x-3 w-full overflow-x-auto overflow-y-hidden">
            {asLink.map((option) => (
              <div
                className={`px-4 py-2 border-b-4 ${
                  location.pathname.includes(option.url)
                    ? "border-b-dark-cyan"
                    : "border-b-transparent"
                }`}
                key={option.url}
                data-url={option.url}
              >
                <Link to={option.url}>
                  <h3 className="flex justify-start items-center gap-x-2 w-max font-semibold text-black text-lg">
                    {option?.icon && (
                      <SvgIcon
                        iconName={option?.icon}
                        className="size-6 svg-icon"
                      />
                    )}
                    {option.title}
                  </h3>
                </Link>
              </div>
            ))}
          </div>
        </div>
        <div>{children}</div>
      </div>
    );
  } else {
    return (
      <div className="w-full tab-box">
        <div className="flex justify-between items-center gap-x-4">
          {getShowButtons && (
            <button onClick={scrollLeft} className="shadow px-1 py-2 border">
              <SvgIcon iconName="LeftArrow" className="size-5" />
            </button>
          )}
          <div
            ref={scrollContainerRef}
            className="border-gray-200 border-b w-full overflow-x-auto no-scrollbar"
          >
            <nav className="flex gap-x-1">
              {React.Children.map(children, (child: any) =>
                !child?.props?.isHide ? (
                  <button
                    type="button"
                    onClick={() => handleTabClick(child.props.eventKey)}
                    className={`pt-4 pb-2 px-3 text-lg inline-flex items-center gap-x-2 border-b-4 whitespace-nowrap focus:outline-none ${
                      child.props.eventKey === activeKey
                        ? "font-bold border-Brown text-black"
                        : "font-semibold border-transparent text-black hover:text-blue-600"
                    }`}
                  >
                    {child.props?.tabIcon && (
                      <SvgIcon
                        iconName={child.props?.tabIcon}
                        className="size-6 svg-icon"
                      />
                    )}
                    {child.props?.title}
                  </button>
                ) : null
              )}
            </nav>
          </div>
          {getShowButtons && (
            <button onClick={scrollRight} className="shadow px-1 py-2 border">
              <SvgIcon iconName="RightArrow" className="size-5" />
            </button>
          )}
        </div>

        <div className="mt-3">
          {React.Children.map(children, (child: any) => {
            if (!child || child.props?.isHide) return null;
            const isActive = child.props.eventKey === activeKey;
            const hasBeenVisited = visitedTabs.has(child.props.eventKey);

            return hasBeenVisited
              ? React.cloneElement(child, { isActive })
              : isActive
                ? React.cloneElement(child, { isActive })
                : null;
          })}
        </div>
      </div>
    );
  }
};

export default TabsBox;
